import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Loader2,
    ArrowRight,
    Sparkles,
    Shield,
    CheckCircle,
    AlertCircle,
    Key,
    Eye,
    EyeOff,
    Plus,
    Trash2,
    Settings,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { CreditModal } from '../../components/ui/credit-modal.js';
import {
    getUserProfile,
    saveSessionSeed,
    getUserCredits,
    updateSessionMeta,
} from '../../lib/supabaseClient.js';
import { PromptKit } from '../../types';
import { arcosSessionService } from '../../api/arcosSession.js';
import { sessionsService } from '../../api/sessions.js';
import { beesClient } from '../../api/beesClient.js';
// import { BUILD_CREDITS_ENABLED } from '../../config/features';
const BUILD_CREDITS_ENABLED = false; // Will be controlled by buildCreditsEnabled state
import { useLLMGoalSummarizer } from '../../hooks/useLLMGoalSummarizer.js';
import { useChatStore } from '../../stores/useChatStore.js';

const promptSuggestions = [
    'Refactor this repo to TypeScript',
    'Write unit tests for my service layer',
    'Migrate to Vite + Tailwind',
    'Generate docs for this project',
    'Fix API errors and add error boundaries',
    'Add authentication and user management',
    'Optimize database queries and performance',
    'Set up CI/CD pipeline with GitHub Actions',
];

const BeehiveStartPage: React.FC = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<PromptKit>({
        goal: '',
        repo: '',
        selectedPlugins: ['Auto'], // Default to Auto mode
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showCreditModal, setShowCreditModal] = useState(false);
    const [userCredits, setUserCredits] = useState<number | null>(null);
    const [envStatus, setEnvStatus] = useState<'unknown' | 'checking' | 'ready' | 'not-ready'>(
        'unknown'
    );
    const [envCheckLoading, setEnvCheckLoading] = useState(false);
    const [bypassEnvironmentValidation, setBypassEnvironmentValidation] = useState(false);
    const [showEnvVariables, setShowEnvVariables] = useState(false);
    const [envVariables, setEnvVariables] = useState<Record<string, string>>({});
    const [envVariableErrors, setEnvVariableErrors] = useState<Record<string, string>>({});
    const [envVariableSaving, setEnvVariableSaving] = useState<Record<string, boolean>>({});
    const [envVariableSuccess, setEnvVariableSuccess] = useState<Record<string, boolean>>({});
    const [maskedEnvVars, setMaskedEnvVars] = useState<Set<string>>(new Set());
    const [buildCreditsEnabled, setBuildCreditsEnabled] = useState(true);

    const {
        summarize,
        loading: summarizing,
        result: summaryResult,
        error: summaryError,
    } = useLLMGoalSummarizer();
    const { setSessionTitle } = useChatStore();

    useEffect(() => {
        checkUserCredits();
        checkEnvironmentStatus();

        const prefetchAssets = async () => {
            try {
                import(/* webpackPrefetch: true */ '../BeehiveWorkspacePage');

                const baseUrl = import.meta.env.VITE_ARCOS_API_URL || 'http://localhost:8080';

                fetch(`${baseUrl}/api/v1/workspace/me`).catch(() => {});

                if (BUILD_CREDITS_ENABLED) {
                    fetch(`${baseUrl}/api/user/credits`).catch(() => {});
                }

                fetch(`${baseUrl}/api/llm/summarize-goal`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ goal: 'prefetch' }),
                }).catch(() => {});
            } catch (error) {
                console.error('Error in prefetchAssets:', error);
            }
        };

        prefetchAssets();
    }, []);

    const checkUserCredits = async () => {
        const profile = await getUserProfile();
        if (profile) {
            try {
                const credits = await getUserCredits(profile.id);
                setUserCredits(credits);
            } catch {
                setUserCredits(profile.createCredits);
            }
        } else {
            setUserCredits(12);
        }
    };

    const checkEnvironmentStatus = async () => {
        try {
            setEnvStatus('checking');
            const response = await beesClient.runEnvBootstrap();

            if (response.success && response.data && typeof response.data === 'object') {
                const data = response.data as any;
                const ready = data.ready;
                setEnvStatus(ready ? 'ready' : 'not-ready');
            } else {
                setEnvStatus('not-ready');
            }
        } catch (error) {
            console.warn('Environment check failed:', error);
            setEnvStatus('not-ready');
        }
    };

    const handleCheckEnvironment = async () => {
        setEnvCheckLoading(true);
        try {
            await checkEnvironmentStatus();
            toast.success('Environment check completed', {
                duration: 3000,
                position: 'top-center',
            });
        } catch {
            toast.error('Environment check failed', {
                duration: 3000,
                position: 'top-center',
            });
        } finally {
            setEnvCheckLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.goal.trim()) {
            return;
        }

        if (buildCreditsEnabled && userCredits !== null && userCredits <= 0) {
            setShowCreditModal(true);
            return;
        }

        if (envStatus !== 'ready' && !bypassEnvironmentValidation) {
            toast.error(
                'Environment validation required before starting session. Please check your environment setup or continue without environments.',
                {
                    duration: 5000,
                    position: 'top-center',
                }
            );
            await checkEnvironmentStatus();
            return;
        }

        setIsLoading(true);

        try {
            const summaryData = await summarize(formData.goal);

            if (summaryData) {
                setSessionTitle(summaryData.sessionName);

                toast.success(`Goal analyzed: ${summaryData.sessionName}`, {
                    duration: 3000,
                    position: 'top-center',
                });
            }
        } catch (summaryError) {
            console.warn('Goal summarization failed:', summaryError);
        }

        try {
            const sessionInitResult = await sessionsService.initSession({
                goal: formData.goal,
                repoUrl: formData.repo || undefined,
                initialMessage: formData.goal,
                rawTitle: summaryResult?.sessionName || formData.goal,
            });

            if (sessionInitResult.success) {
                const sessionId = sessionInitResult.sessionId;

                try {
                    await updateSessionMeta(sessionId, {
                        ...formData,
                        selectedPlugins: sessionInitResult.plugins,
                    });
                } catch (error) {
                    console.warn('Could not save session meta to Supabase:', error);
                }

                const saved = await saveSessionSeed(sessionId, {
                    ...formData,
                    selectedPlugins: sessionInitResult.plugins,
                });
                if (!saved) {
                    console.warn('Failed to save session seed to Supabase');
                }

                const sessionResult = await arcosSessionService.startSession({
                    sessionId,
                    controlMode: 'assist',
                    pluginMemory: sessionInitResult.plugins,
                    promptKit: formData,
                });

                if (sessionResult.status === 'success') {
                    await arcosSessionService.setBeeControlMode(sessionId, 'assist');
                    await arcosSessionService.populatePluginMemory(
                        sessionId,
                        sessionInitResult.plugins
                    );

                    toast.success('Session started successfully! 🚀', {
                        duration: 3000,
                        position: 'top-center',
                    });

                    navigate(`/beehive/workspace?sessionId=${sessionId}`, {
                        state: {
                            sessionTitle: summaryResult?.sessionName || sessionInitResult.title,
                            nextQuestion: sessionInitResult.nextQuestion,
                            plugins: sessionInitResult.plugins,
                            playbook: sessionInitResult.playbook,
                            messages: [
                                ...(sessionInitResult.messages || []),
                                ...(summaryResult
                                    ? [
                                          {
                                              role: 'system',
                                              text: `📋 **Project Summary**: ${summaryResult.summary}\n\n**Key Points**: ${summaryResult.keyPoints.join(', ')}`,
                                          },
                                      ]
                                    : []),
                            ],
                            summary: summaryResult?.summary,
                            keyPoints: summaryResult?.keyPoints,
                        },
                    });
                } else {
                    console.error('Failed to start ARCOS session');
                    toast.error('Failed to start session. Please try again.', {
                        duration: 5000,
                        position: 'top-center',
                    });
                }
            } else {
                console.error('Failed to initialize session');
                toast.error(
                    'Failed to initialize session. Please check your connection and try again.',
                    {
                        duration: 5000,
                        position: 'top-center',
                    }
                );
            }
        } catch (error) {
            console.error('Error starting session:', error);

            if (error instanceof Error) {
                if (
                    error.message.includes('500') ||
                    error.message.includes('Internal Server Error')
                ) {
                    toast.error('Server error occurred. Please try again in a moment.', {
                        duration: 6000,
                        position: 'top-center',
                    });
                } else if (
                    error.message.includes('Network Error') ||
                    error.message.includes('fetch')
                ) {
                    toast.error(
                        'Network connection failed. Please check your internet connection.',
                        {
                            duration: 6000,
                            position: 'top-center',
                        }
                    );
                } else {
                    toast.error('An unexpected error occurred. Please try again.', {
                        duration: 5000,
                        position: 'top-center',
                    });
                }
            } else {
                toast.error('An unexpected error occurred. Please try again.', {
                    duration: 5000,
                    position: 'top-center',
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoToStore = () => {
        navigate('/store');
    };

    const handleSuggestionClick = (suggestion: string) => {
        setFormData({ ...formData, goal: suggestion });
    };

    const addEnvVariable = () => {
        const newKey = `ENV_VAR_${Object.keys(envVariables).length + 1}`;
        setEnvVariables(prev => ({ ...prev, [newKey]: '' }));
    };

    const removeEnvVariable = (key: string) => {
        setEnvVariables(prev => {
            const newVars = { ...prev };
            delete newVars[key];
            return newVars;
        });
        setEnvVariableErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[key];
            return newErrors;
        });
        setEnvVariableSaving(prev => {
            const newSaving = { ...prev };
            delete newSaving[key];
            return newSaving;
        });
        setEnvVariableSuccess(prev => {
            const newSuccess = { ...prev };
            delete newSuccess[key];
            return newSuccess;
        });
        setMaskedEnvVars(prev => {
            const newMasked = new Set(prev);
            newMasked.delete(key);
            return newMasked;
        });
    };

    const saveEnvVariable = async (key: string) => {
        if (!envVariables[key]?.trim()) {
            setEnvVariableErrors(prev => ({ ...prev, [key]: 'Value cannot be empty' }));
            return;
        }

        setEnvVariableSaving(prev => ({ ...prev, [key]: true }));
        setEnvVariableErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[key];
            return newErrors;
        });

        try {
            const response = await beesClient.runEnvBootstrap({
                action: 'save-secret',
                key: key,
                value: envVariables[key],
            });

            if (response.success) {
                setEnvVariableSuccess(prev => ({ ...prev, [key]: true }));
                toast.success(`Environment variable ${key} saved successfully`);
                setTimeout(() => {
                    setEnvVariableSuccess(prev => {
                        const newSuccess = { ...prev };
                        delete newSuccess[key];
                        return newSuccess;
                    });
                }, 3000);
            } else {
                setEnvVariableErrors(prev => ({
                    ...prev,
                    [key]: response.error || 'Failed to save',
                }));
            }
        } catch {
            setEnvVariableErrors(prev => ({
                ...prev,
                [key]: 'Failed to save environment variable',
            }));
        } finally {
            setEnvVariableSaving(prev => {
                const newSaving = { ...prev };
                delete newSaving[key];
                return newSaving;
            });
        }
    };

    const toggleEnvVarVisibility = (key: string) => {
        setMaskedEnvVars(prev => {
            const newMasked = new Set(prev);
            if (newMasked.has(key)) {
                newMasked.delete(key);
            } else {
                newMasked.add(key);
            }
            return newMasked;
        });
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center p-4 relative"
            style={{
                backgroundImage: 'url(/assets/Beehive+Background.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        >
            {/* Full screen overlay to ensure background coverage */}
            <div
                className="absolute inset-0 z-0"
                style={{ backgroundColor: 'rgba(10, 15, 41, 0.15)' }}
            />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="relative z-10 w-full max-w-2xl"
            >
                <div className="bg-black/75 backdrop-blur-sm border border-gray-700 rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 lg:p-12">
                    <div className="text-center mb-8">
                        <motion.img
                            src="/assets/logo.png"
                            alt="Beehive Logo"
                            className="w-14 h-14 mx-auto mb-4"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: 'spring' }}
                        />
                        <motion.h1
                            className="text-2xl font-bold text-white text-drop-shadow mb-1"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                        >
                            Welcome to the Beehive
                        </motion.h1>
                        <motion.h2
                            className="text-lg font-medium mb-3"
                            style={{ color: '#2563EB' }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.35 }}
                        >
                            AI-Powered Workspace
                        </motion.h2>
                        <motion.p
                            className="text-gray-400 text-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                        >
                            Start by telling Bee what you're working on. We'll set up your
                            environment, load the right plugins, and guide your next move.
                        </motion.p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.5 }}
                        >
                            <label
                                htmlFor="goal"
                                className="block text-sm font-medium text-white mb-2"
                            >
                                What's your goal today?
                            </label>
                            <p className="text-xs text-gray-400 mb-2">
                                Share your objective, issue, or task in your own words — Bee will
                                handle the rest.
                            </p>
                            <textarea
                                id="goal"
                                data-devinid="goal-input"
                                value={formData.goal}
                                onChange={e => setFormData({ ...formData, goal: e.target.value })}
                                placeholder="Describe your development goal or task..."
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 focus:shadow-lg focus:shadow-slate-400/20 resize-none transition-all duration-200"
                                rows={4}
                                required
                            />

                            {!formData.goal.trim() && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="mt-4"
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <Sparkles className="w-4 h-4 text-slate-400" />
                                        <span className="text-sm text-gray-400">
                                            Quick suggestions:
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto">
                                        {promptSuggestions.map((suggestion, index) => (
                                            <motion.button
                                                key={suggestion}
                                                type="button"
                                                onClick={() => handleSuggestionClick(suggestion)}
                                                className="px-3 py-1.5 text-xs bg-white/5 hover:bg-white/15 border border-white/15 hover:border-slate-500 rounded-full text-gray-300 hover:text-slate-300 transition-all duration-200 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: 0.1 * index }}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                            >
                                                {suggestion}
                                            </motion.button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            <label
                                htmlFor="repo"
                                className="block text-sm font-medium text-white mb-2"
                            >
                                Link a Codebase (Optional)
                            </label>
                            <p className="text-xs text-gray-400 mb-2">
                                If you're working in a repo, drop the link and branch name here so
                                Bee can preload context.
                            </p>
                            <input
                                id="repo"
                                data-devinid="repo-input"
                                type="text"
                                value={formData.repo}
                                onChange={e => setFormData({ ...formData, repo: e.target.value })}
                                placeholder="https://github.com/username/repo or repo-name/branch"
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 focus:shadow-lg focus:shadow-slate-400/20 transition-all duration-200"
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.7 }}
                        >
                            <div className="bg-slate-600/10 border border-slate-500/20 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <Sparkles className="w-4 h-4 text-slate-400" />
                                    <span className="text-sm font-medium text-slate-400">
                                        Auto Mode Enabled
                                    </span>
                                </div>
                                <p className="text-xs text-gray-300">
                                    Bee will automatically select the best tools and plugins based
                                    on your goal and codebase. No manual configuration needed!
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.75 }}
                        >
                            <div
                                className={`border rounded-lg p-3 ${
                                    envStatus === 'ready'
                                        ? 'bg-green-600/10 border-green-500/20'
                                        : envStatus === 'not-ready'
                                          ? 'bg-red-600/10 border-red-500/20'
                                          : envStatus === 'checking'
                                            ? 'bg-yellow-600/10 border-yellow-500/20'
                                            : 'bg-gray-600/10 border-gray-500/20'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm font-medium text-gray-300">
                                            Environment Status
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {envStatus === 'ready' && (
                                            <CheckCircle className="w-4 h-4 text-green-400" />
                                        )}
                                        {envStatus === 'not-ready' && (
                                            <AlertCircle className="w-4 h-4 text-red-400" />
                                        )}
                                        {envStatus === 'checking' && (
                                            <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
                                        )}
                                        <span
                                            className={`text-xs font-medium ${
                                                envStatus === 'ready'
                                                    ? 'text-green-400'
                                                    : envStatus === 'not-ready'
                                                      ? 'text-red-400'
                                                      : envStatus === 'checking'
                                                        ? 'text-yellow-400'
                                                        : 'text-gray-400'
                                            }`}
                                        >
                                            {envStatus === 'ready'
                                                ? 'Ready'
                                                : envStatus === 'not-ready'
                                                  ? 'Not Ready'
                                                  : envStatus === 'checking'
                                                    ? 'Checking...'
                                                    : 'Unknown'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <p className="text-xs text-gray-300">
                                        {envStatus === 'ready'
                                            ? 'All required secrets and dependencies are configured.'
                                            : envStatus === 'not-ready'
                                              ? 'Some secrets or dependencies need configuration.'
                                              : envStatus === 'checking'
                                                ? 'Validating environment setup...'
                                                : 'Click to check your environment setup.'}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleCheckEnvironment}
                                        disabled={envCheckLoading || envStatus === 'checking'}
                                        className="px-3 py-1 text-xs bg-white/10 hover:bg-white/20 border border-white/20 rounded text-gray-300 hover:text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {envCheckLoading || envStatus === 'checking' ? (
                                            <>
                                                <Loader2 className="w-3 h-3 mr-1 animate-spin inline" />
                                                Checking...
                                            </>
                                        ) : (
                                            'Check Environment'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>

                        {/* Environment Variables Configuration */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.76 }}
                        >
                            <div className="border border-gray-600/50 rounded-lg p-3 bg-gray-600/10">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <Key className="w-4 h-4 text-slate-400" />
                                        <span className="text-sm font-medium text-gray-300">
                                            Environment Variables
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowEnvVariables(!showEnvVariables)}
                                        className="text-xs text-slate-400 hover:text-slate-300 transition-colors flex items-center gap-1"
                                    >
                                        <Settings className="w-3 h-3" />
                                        {showEnvVariables ? 'Hide' : 'Configure'}
                                    </button>
                                </div>

                                {showEnvVariables && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mt-3 space-y-3"
                                    >
                                        {Object.entries(envVariables).map(([key, value]) => (
                                            <div key={key} className="space-y-2">
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={key}
                                                        onChange={e => {
                                                            const newKey = e.target.value;
                                                            setEnvVariables(prev => {
                                                                const newVars = { ...prev };
                                                                delete newVars[key];
                                                                newVars[newKey] = value;
                                                                return newVars;
                                                            });
                                                        }}
                                                        placeholder="Variable name"
                                                        className={`flex-1 px-3 py-2 bg-gray-900/50 border rounded-lg text-sm text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors ${
                                                            envVariableErrors[key]
                                                                ? 'border-red-500 focus:ring-red-500'
                                                                : envVariableSuccess[key]
                                                                  ? 'border-green-500 focus:ring-green-500'
                                                                  : 'border-gray-600 focus:ring-slate-500'
                                                        }`}
                                                    />
                                                    <div className="relative flex-1">
                                                        <input
                                                            type={
                                                                maskedEnvVars.has(key)
                                                                    ? 'password'
                                                                    : 'text'
                                                            }
                                                            value={value}
                                                            onChange={e =>
                                                                setEnvVariables(prev => ({
                                                                    ...prev,
                                                                    [key]: e.target.value,
                                                                }))
                                                            }
                                                            placeholder="Variable value"
                                                            className={`w-full px-3 py-2 pr-10 bg-gray-900/50 border rounded-lg text-sm text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors ${
                                                                envVariableErrors[key]
                                                                    ? 'border-red-500 focus:ring-red-500'
                                                                    : envVariableSuccess[key]
                                                                      ? 'border-green-500 focus:ring-green-500'
                                                                      : 'border-gray-600 focus:ring-slate-500'
                                                            }`}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                toggleEnvVarVisibility(key)
                                                            }
                                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                                                        >
                                                            {maskedEnvVars.has(key) ? (
                                                                <EyeOff className="w-4 h-4" />
                                                            ) : (
                                                                <Eye className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => saveEnvVariable(key)}
                                                        disabled={
                                                            envVariableSaving[key] || !value.trim()
                                                        }
                                                        className="px-3 py-2 bg-slate-600 hover:bg-slate-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs rounded-lg transition-colors flex items-center gap-1"
                                                    >
                                                        {envVariableSaving[key] ? (
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                        ) : envVariableSuccess[key] ? (
                                                            <CheckCircle className="w-3 h-3" />
                                                        ) : (
                                                            'Save'
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeEnvVariable(key)}
                                                        className="px-2 py-2 text-red-400 hover:text-red-300 transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                {envVariableErrors[key] && (
                                                    <p className="text-xs text-red-400 flex items-center gap-1">
                                                        <AlertCircle className="w-3 h-3" />
                                                        {envVariableErrors[key]}
                                                    </p>
                                                )}
                                                {envVariableSuccess[key] && (
                                                    <p className="text-xs text-green-400 flex items-center gap-1">
                                                        <CheckCircle className="w-3 h-3" />
                                                        Environment variable saved successfully
                                                    </p>
                                                )}
                                            </div>
                                        ))}

                                        <button
                                            type="button"
                                            onClick={addEnvVariable}
                                            className="w-full px-3 py-2 border border-dashed border-gray-600 rounded-lg text-sm text-gray-400 hover:text-gray-300 hover:border-gray-500 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Environment Variable
                                        </button>

                                        <div className="text-xs text-gray-500 bg-gray-900/30 rounded-lg p-3">
                                            <p className="font-medium mb-1">Security Guidelines:</p>
                                            <ul className="space-y-1 text-gray-400">
                                                <li>
                                                    • Use environment variables for API keys and
                                                    sensitive data
                                                </li>
                                                <li>
                                                    • Variables are encrypted and stored securely
                                                </li>
                                                <li>• Click the eye icon to toggle visibility</li>
                                            </ul>
                                        </div>
                                    </motion.div>
                                )}
                            </div>
                        </motion.div>

                        {/* LLM Summarization Results */}
                        {(summarizing || summaryResult || summaryError) && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                className="mt-4"
                            >
                                <div
                                    className={`border rounded-lg p-4 ${
                                        summaryError
                                            ? 'bg-red-600/10 border-red-500/20'
                                            : summaryResult
                                              ? 'bg-slate-600/10 border-slate-500/20'
                                              : 'bg-yellow-600/10 border-yellow-500/20'
                                    }`}
                                >
                                    {summarizing && (
                                        <motion.div
                                            className="flex items-center gap-2"
                                            animate={{ opacity: [0.5, 1, 0.5] }}
                                            transition={{ repeat: Infinity, duration: 2 }}
                                        >
                                            <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
                                            <span className="text-sm text-yellow-400">
                                                Analyzing your goal...
                                            </span>
                                        </motion.div>
                                    )}

                                    {summaryResult && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.2 }}
                                        >
                                            <div className="flex items-center gap-2 mb-3">
                                                <Sparkles className="w-4 h-4 text-slate-400" />
                                                <span className="text-sm font-medium text-slate-400">
                                                    {summaryResult.sessionName}
                                                </span>
                                                {summaryResult.fallback && (
                                                    <span className="text-xs text-gray-400">
                                                        (fallback)
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-300 mb-3">
                                                {summaryResult.summary}
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {summaryResult.keyPoints.map((point, index) => (
                                                    <motion.span
                                                        key={index}
                                                        initial={{ opacity: 0, scale: 0.8 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        transition={{ delay: 0.3 + index * 0.1 }}
                                                        className="px-2 py-1 text-xs bg-slate-500/20 text-slate-300 rounded-full border border-slate-500/30"
                                                    >
                                                        {point}
                                                    </motion.span>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}

                                    {summaryError && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="flex items-center gap-2"
                                        >
                                            <AlertCircle className="w-4 h-4 text-red-400" />
                                            <span className="text-sm text-red-400">
                                                {summaryError.message}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => summarize(formData.goal)}
                                                className="ml-2 text-xs text-slate-400 hover:text-slate-300 transition-colors"
                                            >
                                                Retry
                                            </button>
                                        </motion.div>
                                    )}
                                </div>
                            </motion.div>
                        )}

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8 }}
                            className="pt-2"
                        >
                            <button
                                type="submit"
                                data-devinid="start-bee-button"
                                disabled={
                                    isLoading ||
                                    summarizing ||
                                    !formData.goal.trim() ||
                                    (envStatus === 'not-ready' && !bypassEnvironmentValidation)
                                }
                                className="w-full bg-slate-600 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed py-3 text-lg font-medium rounded-lg text-white transition-colors flex items-center justify-center"
                                aria-label={
                                    isLoading
                                        ? 'Starting session...'
                                        : summarizing
                                          ? 'Analyzing goal...'
                                          : 'Start Bee Session'
                                }
                            >
                                {isLoading || summarizing ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {summarizing ? 'Analyzing Goal...' : 'Starting Session...'}
                                    </>
                                ) : (
                                    <>
                                        Start Bee Session
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </>
                                )}
                            </button>

                            {!formData.goal.trim() && (
                                <p
                                    role="alert"
                                    className="text-yellow-300 text-sm mt-2 text-center"
                                >
                                    Please enter a goal before starting.
                                </p>
                            )}
                            {envStatus === 'not-ready' && !bypassEnvironmentValidation && (
                                <p role="alert" className="text-red-300 text-sm mt-2 text-center">
                                    Environment validation required. Please check your environment
                                    setup.
                                </p>
                            )}

                            {envStatus === 'not-ready' && (
                                <div className="mt-3 flex items-center justify-center">
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={bypassEnvironmentValidation}
                                            onChange={e =>
                                                setBypassEnvironmentValidation(e.target.checked)
                                            }
                                            className="w-4 h-4 text-slate-600 bg-gray-800 border-gray-600 rounded focus:ring-slate-500 focus:ring-2"
                                        />
                                        <span className="text-sm text-gray-300">
                                            Bypass environment validation (for development)
                                        </span>
                                    </label>
                                </div>
                            )}
                        </motion.div>
                    </form>

                    {userCredits !== null && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.9 }}
                            className="mt-8 text-center"
                        >
                            <div className="flex items-center justify-center gap-4">
                                <p className="text-sm text-gray-300">
                                    Available Build Credits:{' '}
                                    <span className="text-slate-400 font-medium">
                                        {userCredits}
                                    </span>
                                </p>
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={buildCreditsEnabled}
                                        onChange={e => setBuildCreditsEnabled(e.target.checked)}
                                        className="w-4 h-4 text-slate-600 bg-gray-800 border-gray-600 rounded focus:ring-slate-500 focus:ring-2"
                                    />
                                    <span className="text-xs text-gray-400">
                                        Enable build credits
                                    </span>
                                </label>
                            </div>
                            {!buildCreditsEnabled && (
                                <p className="text-xs text-yellow-400 mt-1">
                                    Build credits disabled - unlimited usage for development
                                </p>
                            )}
                        </motion.div>
                    )}
                </div>
            </motion.div>

            <CreditModal
                isOpen={showCreditModal}
                onClose={() => setShowCreditModal(false)}
                onGoToStore={handleGoToStore}
            />
        </div>
    );
};

export default BeehiveStartPage;
